import { cx } from './cx';
import { Text } from './Text';
import type { EnvHealth } from './EntityCard';
import { useT } from '../../modules/i18n';

export interface EnvRelease {
  env: string;
  /** The release tag deployed in this environment, e.g. "v1.4.2". */
  tag?: string;
  status?: EnvHealth;
}

const healthDot: Record<EnvHealth, string> = {
  healthy: 'bg-success',
  degraded: 'bg-warning',
  unknown: 'bg-fg-muted',
};

export interface EnvReleaseTagsProps {
  items: EnvRelease[];
  /** Hide environments that have no release tag (default true). */
  hideEmpty?: boolean;
  className?: string;
}

/**
 * Per-environment release tags grouped into a single column — one row per
 * environment: a health dot, the environment name, and the deployed tag in
 * mono (tabular). Used as a catalog table column so a component's release
 * state across pre-prod / prod reads at a glance. By default, environments
 * without a release tag are hidden (`hideEmpty`).
 */
export const EnvReleaseTags = ({
  items,
  hideEmpty = true,
  className,
}: EnvReleaseTagsProps) => {
  const t = useT();
  const shown = hideEmpty ? items.filter(item => Boolean(item.tag)) : items;
  if (shown.length === 0) {
    return (
      <Text variant="body" tone="muted">
        —
      </Text>
    );
  }
  return (
    <div className={cx('flex flex-col gap-1', className)}>
      {shown.map(item => (
        <div
          key={item.env}
          className="flex items-center gap-3 whitespace-nowrap"
        >
          <span className="flex w-28 shrink-0 items-center gap-1.5">
            {item.status && (
              <span
                className={cx(
                  'size-1.5 shrink-0 rounded-full',
                  healthDot[item.status],
                )}
              />
            )}
            <Text variant="caption" tone="tertiary" className="truncate">
              {item.env}
            </Text>
          </span>
          <Text variant="caption" mono tone={item.tag ? 'secondary' : 'muted'}>
            {item.tag ?? t('ui.envReleaseTags.none')}
          </Text>
        </div>
      ))}
    </div>
  );
};
